import {ChangeEvent, FC, createContext, useContext, useEffect, useState} from 'react'
import {
  CREATE_BULK_SENDER_IDS_URL,
  CREATE_MASKING_SENDER_URL,
  CREATE_NON_MASKING_SENDER_URL,
  GET_SENDERIDS_URL,
  ISenderIdArray,
  MASRK_DEFAULT_SENDER_URL,
  MaskDataTypes,
  NonMaskDataTypes,
  Route,
  SenderIdProviderProps,
  UPDATE_BRANDED_URL,
  UPDATE_BULK_NON_BRANDED_URL,
  UPDATE_NON_BRANDED_URL,
} from '../core/_model'
import {useAuth} from '../../auth'
import {ISenderId} from '../SettingsModel'
import toast from 'react-hot-toast'
import axios, {AxiosError} from 'axios'
import {getRouteList} from '../SMSBroadCastRequest'
import * as XLSX from 'xlsx'
import {saveAs} from 'file-saver'
import {Params, UploadDetailsResponse} from '../Sender/_model'
import {showErrorToast, showInfoToast, showSuccessToast} from '../../../utils/helperFunction/toast'
import {dateFormat} from '../../../utils/helperFunction/_helper'

export const SenderIdContext = createContext<any>(null)

export const SenderIdProvider: FC<SenderIdProviderProps> = ({children}) => {
  const {auth, currentUser} = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [options, setOptions] = useState<Route[]>([])
  const [error, setError] = useState({isError: true, message: ''})
  const [senderData, setSenderData] = useState<ISenderIdArray>([])
  const [filteredSender, setFilteredSender] = useState<ISenderIdArray>([])
  const [updateSender, setUpdateSender] = useState<ISenderId | undefined>(undefined)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [apiError, setApiError] = useState({isError: false, message: ''})
  const [uploadResponse, setUploadResponse] = useState<UploadDetailsResponse | null>(null)
  const [senderStatus, setSenderStatus] = useState<string | undefined>(undefined)

  // const [currentPage, setCurrentPage] = useState(1)
  // const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalElement, setTotalElement] = useState(0)
  const [isLastPage, setIsLastPage] = useState(false)

  const [parameters, setParameters] = useState<Params>({
    pageNumber: 1,
    pageSize: 10,
  })

  const isAdmin = currentUser?.author_id === 0
  const isUserP2p = currentUser?.isP2p === '1'

  const handlePageChange = (pageNumber: number) => {
    // setCurrentPage(pageNumber)
    setParameters((prevState) => ({
      ...prevState,
      pageNumber,
    }))
  }

  const handleItemsPerPage = (size: number) => {
    setParameters((prevState) => ({
      ...prevState,
      pageSize: size,
      pageNumber: 1,
    }))
  }

  const handleFilterValue = (type: string, value: number) => {
    const isValid = value !== undefined && value !== null && !Number.isNaN(value)

    setParameters((prevState) => {
      const updated = {...prevState, pageNumber: 1}

      switch (type) {
        case 'status':
          if (isValid) {
            updated.senderStatus = value
          } else {
            delete updated.senderStatus
          }
          break

        case 'route':
          if (isValid) {
            updated.routeid = value
          } else {
            delete updated.routeid
          }
          break

        case 'branded':
          if (isValid) {
            updated.branded = value
          } else {
            delete updated.branded
          }
          break

        default:
          break
      }

      return updated
    })
  }

  const handleSenderFilter = (value: string) => {
    const isValid = value !== undefined && value !== null && value.trim() !== ''

    setParameters((prevState) => {
      const updated = {...prevState, pageNumber: 1}

      if (isValid) {
        updated.sender = value
      } else {
        delete updated.sender
      }

      return updated
    })
  }

  const onResetFilter = () => {
    setParameters({pageNumber: 1, pageSize: 10})
  }

  // let setParams: {[key: string]: any} = {
  //   pageNumber: currentPage - 1,
  //   pageSize: itemsPerPage,
  // }

  const getSenderIds = async (params = parameters) => {
    try {
      setIsLoading(true)
      toast.loading('Getting Sender Ids..')
      const response = await axios.get(`${GET_SENDERIDS_URL}/${currentUser?.id}`, {
        headers: {Authorization: `Bearer ${auth?.accessToken}`},
        params: {
          ...params,
          pageNumber: params.pageNumber - 1,
          ...(isAdmin ? {} : isUserP2p ? {branded: 2} : {branded: 0}),
        },
      })
      const data = await response.data
      const result = await data.data
      const totalResult = await data.totalElement
      const lastPage = await data.lastpage
      setIsLastPage(lastPage)
      setTotalElement(totalResult)
      setSenderData(result)
      setIsLoading(false)
      setError({isError: false, message: ''})
      toast.dismiss()
      toast.success('Fetched Sender Ids')
    } catch (err) {
      const errorMessage = (err as Error)?.message || 'An error occurred'
      setIsLoading(false)
      setError({isError: true, message: errorMessage})
      toast.dismiss()
      toast.error(`Error: ${errorMessage}`)
    }
  }

  const markDefaultSender = async (id: number) => {
    try {
      toast.loading('Processing..')
      const response = await axios.get(`${MASRK_DEFAULT_SENDER_URL}/${id}?senderId=${id}`, {
        headers: {Authorization: `Bearer ${auth?.accessToken}`},
      })
      toast.dismiss()
      getSenderIds()
      return response
    } catch (err) {
      const errorMessage = (err as Error)?.message || 'An error occurred'
      setIsLoading(false)
      setError({isError: true, message: errorMessage})
    }
  }

  const createMaskingSender = async (createMaskData: MaskDataTypes) => {
    try {
      toast.loading('Creating Masking Sender Id..')
      const res = await axios.post(CREATE_MASKING_SENDER_URL, createMaskData, {
        headers: {Authorization: `Bearer ${auth?.accessToken}`},
      })
      const data = await res.data
      toast.dismiss()
      toast.success('Successfully created masking sender id')
      getSenderIds()
      return data
    } catch (error: any) {
      if (error.response) {
        const errorMessage = error.response.data.message
        setApiError({isError: true, message: errorMessage})
        toast.dismiss()
      } else {
        toast.dismiss()
        toast.error(`Error: ${error.message}`)
      }
    }
  }

  const createNonMaskingSender = async (createNonMaskData: NonMaskDataTypes) => {
    try {
      toast.loading('Create Non Masking Sender')
      const res = await axios.post(CREATE_NON_MASKING_SENDER_URL, createNonMaskData, {
        headers: {Authorization: `Bearer ${auth?.accessToken}`},
      })
      const data = await res.data
      getSenderIds()
      toast.dismiss()
      toast.success('Successfully created non masking sender')
      console.log(res)
      return data
    } catch (error: any) {
      if (error.response) {
        const errorMessage = error.response.data.message
        setApiError({isError: true, message: errorMessage})
        toast.dismiss()
      } else {
        console.log(error.message)
        toast.dismiss()
        toast.error(`Error: ${error.message}`)
      }
    }
  }

  const updateNonBranded = async (id: number, updatedNonBranded: NonMaskDataTypes) => {
    try {
      toast.loading('Updating..')
      const res = await axios.put(`${UPDATE_NON_BRANDED_URL}/${id}`, updatedNonBranded, {
        headers: {Authorization: `Bearer ${auth?.accessToken}`},
      })
      const data = await res.data
      getSenderIds()
      toast.dismiss()
      toast.success('Update successfully')
      return data
    } catch (err: any) {
      console.log(err)
      toast.dismiss()
      toast.error(`Error: ${err.message}`)
    }
  }

  const updateBranded = async (id: number, updateBranded: MaskDataTypes) => {
    try {
      toast.loading('Updating..')
      const res = await axios.put(`${UPDATE_BRANDED_URL}/${id}`, updateBranded, {
        headers: {Authorization: `Bearer ${auth?.accessToken}`},
      })
      const data = await res.data
      getSenderIds()
      toast.dismiss()
      toast.success('Update successfully')
      return data
    } catch (err: any) {
      console.log(err)
      toast.dismiss()
      toast.error(`Error: ${err.message}`)
    }
  }

  const createBulkSenderIds = async () => {
    try {
      toast.loading('Processing..')
      const response = await axios.post(
        `${CREATE_BULK_SENDER_IDS_URL}/${uploadResponse?.userId}/${uploadResponse?.routeconfId}/${uploadResponse?.id}`,
        {
          headers: {
            Authorization: `Bearer ${auth?.accessToken}`,
          },
        }
      )

      console.log(response)
      if (response.status === 200) {
        getSenderIds()
        setUploadResponse(null)
        toast.dismiss()
        toast.success('Successfully addedd..')
      } else {
        toast.dismiss()
        toast.error('Something went wrong try again')
      }
    } catch (error) {
      console.log(error)
      toast.dismiss()
      toast.error('Something went wrong try again')
    }
  }

  const updateBulkNonBranded = async (sender: string[], senderIds: number[], routeId: number) => {
    try {
      toast.loading('Processing..')
      const response = await axios.put(
        UPDATE_BULK_NON_BRANDED_URL,
        {sender, senderIds, routeId},
        {
          headers: {
            Authorization: `Bearer ${auth?.accessToken}`,
          },
        }
      )
      console.log(response)
      if (response.status === 201) {
        getSenderIds()
        const message = response.data
        toast.dismiss()
        toast.success(message)
      } else {
        const message = response.data
        toast.dismiss()
        toast.error(message)
      }
    } catch (error: any) {
      console.log(error)
      toast.dismiss()
      toast.error(error.message)
    }
  }

  const hide = () => {
    setUpdateSender(undefined)
  }

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  let showPageLastEtries = !isLastPage
    ? parameters.pageNumber * parameters.pageNumber
    : totalElement
  let showPageFirstEntries = !isLastPage
    ? showPageLastEtries + 1 - parameters.pageNumber
    : (parameters.pageNumber - 1) * parameters.pageNumber + 1

  let serial: number[] = []

  for (let i = showPageFirstEntries; i <= showPageLastEtries; i++) {
    serial.push(i)
  }

  const downloadSenderIds = () => {
    const filtered = filteredSender.map((sender) => {
      const {
        id,
        assignedby,
        assignedto,
        routeconfid,
        markAsDefault,
        routeName,
        senderStatus,
        branded,
        ...sendvalues
      } = sender

      return {
        ...sendvalues,
        status: senderStatus === '1' ? 'Active' : 'Inactive',
        branded: branded === 1 ? 'Masking' : 'Non-Masking',
        assigneddate: dateFormat(sender.assigneddate, true),
      }
    })

    const worksheet = XLSX.utils.json_to_sheet(filtered)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')

    // Buffer to store the generated Excel file
    const excelBuffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'})
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    })

    saveAs(blob, 'senderids.xlsx')
  }

  useEffect(() => {
    getSenderIds()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameters])

  useEffect(() => {
    getRouteList(auth?.accessToken)
      .then((res) => {
        setOptions(res)
      })
      .catch((err) => {
        console.log(err)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const filteredData = senderData?.filter((sender) =>
      sender.sender.toLowerCase().includes(searchTerm.toLowerCase())
    )
    // console.log(filteredData)
    setFilteredSender(filteredData || [])
  }, [senderData, searchTerm])

  const values = {
    options,
    isLoading,
    senderData,
    filteredSender,
    updateSender,
    apiError,
    setApiError,
    totalElement,
    isLastPage,
    searchTerm,
    setSearchTerm,
    handlePageChange,
    getSenderIds,
    markDefaultSender,
    createMaskingSender,
    createNonMaskingSender,
    updateBranded,
    updateNonBranded,
    setUpdateSender,
    handleSearch,
    downloadSenderIds,
    serial,
    hide,
    uploadResponse,
    setUploadResponse,
    createBulkSenderIds,
    updateBulkNonBranded,
    setSenderStatus,
    senderStatus,
    handleItemsPerPage,
    parameters,
    handleFilterValue,
    onResetFilter,
    handleSenderFilter,
  }
  return <SenderIdContext.Provider value={values}>{children}</SenderIdContext.Provider>
}

export const useSenderIdProvider = () => useContext(SenderIdContext)
